/**
 * Inventory Chat Widget
 *
 * Main chat widget with floating action button (FAB).
 * Provides AI-powered inventory assistance with tool calling.
 * CP Admin 2077 styling with orange/cyan accents.
 *
 * Features:
 * - Floating FAB that expands to full chat
 * - Cmd+K keyboard shortcut
 * - Suggested actions from AI
 * - Message history with tool call display
 * - Responsive design
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { MessageSquare, X, Loader2, Sparkles, RotateCcw, AlertCircle } from 'lucide-react';
import { useChat, type SuggestedAction } from '../../contexts/ChatContext';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { trpc } from '../../lib/trpc/client';

export function InventoryChat() {
  const {
    isOpen,
    sessionId,
    messages,
    isLoading,
    error,
    suggestedActions,
    toggleChat,
    closeChat,
    setSession,
    clearSession,
    addMessage,
    setMessages,
    setLoading,
    setError,
    setSuggestedActions,
  } = useChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sendMessageMutation = trpc.chat.sendMessage.useMutation();
  const getHistoryQuery = trpc.chat.getHistory.useQuery(
    { sessionId: sessionId || '' },
    { enabled: !!sessionId }
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Load history when session changes
  useEffect(() => {
    if (getHistoryQuery.data?.messages) {
      setMessages(
        getHistoryQuery.data.messages.map((msg) => ({
          id: msg.id,
          role: msg.role as 'user' | 'assistant' | 'system' | 'tool',
          content: msg.content,
          toolCalls: msg.toolCalls as any[],
          createdAt: new Date(msg.createdAt),
        }))
      );
    }
  }, [getHistoryQuery.data, setMessages]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Add user message optimistically
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user' as const,
      content,
      createdAt: new Date(),
    };
    addMessage(userMessage);
    setLoading(true);
    setError(null);

    try {
      const result = await sendMessageMutation.mutateAsync({
        message: content,
        sessionId: sessionId || undefined,
      });

      // Set session if this is the first message
      if (result.sessionId && !sessionId) {
        setSession(result.sessionId);
      }

      // Add assistant response
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant' as const,
        content: result.response,
        toolCalls: result.toolsCalled?.map((tc) => ({
          toolName: tc.toolName,
          args: tc.args as Record<string, unknown>,
          result: tc.result,
        })),
        createdAt: new Date(),
      };
      addMessage(assistantMessage);

      // Set suggested actions
      if (result.suggestedActions) {
        setSuggestedActions(result.suggestedActions as SuggestedAction[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  }, [sessionId, addMessage, setSession, setLoading, setError, setSuggestedActions, sendMessageMutation]);

  const handleSuggestedAction = useCallback((action: SuggestedAction) => {
    // Convert suggested action to a message
    handleSendMessage(action.action);
  }, [handleSendMessage]);

  const handleNewChat = useCallback(() => {
    clearSession();
    setSuggestedActions([]);
  }, [clearSession, setSuggestedActions]);

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="
            fixed bottom-6 right-6 z-50
            flex items-center justify-center
            w-14 h-14
            bg-gradient-to-r from-orange-500 to-orange-600
            text-white
            shadow-lg shadow-orange-500/30
            hover:from-orange-400 hover:to-orange-500
            hover:scale-105
            active:scale-95
            transition-all duration-200
            group
          "
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)',
          }}
          aria-label="Open inventory chat (Cmd+K)"
          title="Open inventory chat (Cmd+K)"
        >
          <MessageSquare className="w-6 h-6" />
          {/* Keyboard hint on hover */}
          <div className="
            absolute -top-8 right-0
            px-2 py-1 bg-slate-800 text-xs text-slate-300
            opacity-0 group-hover:opacity-100
            transition-opacity duration-200
            whitespace-nowrap
          ">
            ⌘K
          </div>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="
            fixed bottom-6 right-6 z-50
            flex flex-col
            w-[400px] max-w-[calc(100vw-3rem)]
            h-[600px] max-h-[calc(100vh-6rem)]
            bg-slate-900
            border border-slate-700
            shadow-2xl shadow-black/50
          "
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%)',
          }}
        >
          {/* Header */}
          <div className="
            flex items-center justify-between
            px-4 py-3
            bg-gradient-to-r from-slate-800 to-slate-900
            border-b border-slate-700
          ">
            <div className="flex items-center gap-3">
              <div className="
                flex items-center justify-center
                w-8 h-8
                bg-gradient-to-r from-orange-500 to-orange-600
              "
              style={{
                clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%)',
              }}
              >
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100">
                  Inventory Assistant
                </h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                  AI-Powered
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* New Chat Button */}
              <button
                onClick={handleNewChat}
                className="
                  p-2 text-slate-400
                  hover:text-orange-400 hover:bg-slate-800
                  transition-colors duration-200
                "
                title="Start new chat"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              {/* Close Button */}
              <button
                onClick={closeChat}
                className="
                  p-2 text-slate-400
                  hover:text-red-400 hover:bg-slate-800
                  transition-colors duration-200
                "
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1">
            {/* Welcome message if no messages */}
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="
                  w-16 h-16 mb-4
                  flex items-center justify-center
                  bg-gradient-to-r from-orange-500/20 to-orange-600/20
                  border border-orange-500/30
                "
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)',
                }}
                >
                  <MessageSquare className="w-8 h-8 text-orange-500" />
                </div>
                <h4 className="text-lg font-semibold text-slate-200 mb-2">
                  How can I help?
                </h4>
                <p className="text-sm text-slate-500 max-w-[280px]">
                  Ask me about your inventory - search items, get stats, find products by condition or location.
                </p>

                {/* Starter prompts */}
                <div className="mt-6 space-y-2 w-full">
                  {[
                    'How many items are in inventory?',
                    'Show items by category',
                    'Find vintage cameras',
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleSendMessage(prompt)}
                      className="
                        w-full px-4 py-2
                        text-left text-sm text-slate-300
                        bg-slate-800/50 border border-slate-700
                        hover:bg-slate-800 hover:border-orange-500/50
                        transition-colors duration-200
                      "
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message list */}
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center gap-2 text-slate-400 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Thinking...</span>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="
                flex items-start gap-2
                px-3 py-2
                bg-red-900/20 border border-red-700/50
                text-red-300 text-sm
              ">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Actions */}
          {suggestedActions.length > 0 && !isLoading && (
            <div className="px-4 py-2 border-t border-slate-700/50 bg-slate-900/50">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">
                Suggestions
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedActions.slice(0, 3).map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedAction(action)}
                    className="
                      px-3 py-1.5
                      text-xs text-cyan-300
                      bg-cyan-900/30 border border-cyan-700/50
                      hover:bg-cyan-900/50 hover:border-cyan-500
                      transition-colors duration-200
                    "
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <ChatInput
            onSend={handleSendMessage}
            disabled={isLoading}
          />
        </div>
      )}
    </>
  );
}

export default InventoryChat;
